import React, { useState } from 'react'
import { Typography, CardMedia, Skeleton } from '@mui/material'
import { AccentArea } from 'components'
import { Topic } from './Help.vm'
import VideoModal from './VideoModal'

interface VideoProps {
    src: string
    type: string
    className?: string
    poster?: string
}
// Contain a html5 video with a single src (preview mode - no controls)
const Video = ({
    src,
    type,
    className = '',
    poster,
}: VideoProps): React.ReactElement => (
    <video
        className={`${className} emp-video`}
        preload="metadata"
        style={{ pointerEvents: 'none' }}
        poster={poster}
    >
        <source src={src} type={type} />
        <track kind="captions" srcLang="en" label="English" />
        Unable to view the video in this browser, please try with another.
    </video>
)
/**
 * Use to display a single topic and video
 */
const TopicArea = ({
    topic,
    loading,
    disabledMessage,
}: {
    topic: Topic
    loading: boolean
    disabledMessage?: string
}): React.ReactElement => {
    const [isModalOpen, setIsModalOpen] = useState(false)

    const handleOpenModal = () => {
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
    }

    return (
        <div id="helpTopic" className="c-help-center-topic">
            <AccentArea>
                <div className="c-help-center-topic__header">
                    <div className="c-help-center-topic__header-title">
                        {topic.title}
                    </div>
                    <div className="c-help-center-topic__header-description">
                        {topic.category}
                    </div>
                </div>
                <CardMedia className="c-help-center-topic__video">
                    {!loading && topic?.videoUrl && topic?.videoType ? (
                        <div
                            onClick={handleOpenModal}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    handleOpenModal()
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            style={{
                                cursor: 'pointer',
                                position: 'relative',
                                pointerEvents: disabledMessage
                                    ? 'none'
                                    : 'auto',
                            }}
                            aria-label={`Play video: ${topic.title}`}
                        >
                            <div
                                style={{
                                    position: 'relative',
                                    isolation: 'isolate',
                                }}
                            >
                                <Video
                                    src={`${process.env.PUBLIC_URL}/help/${topic.videoUrl}`}
                                    type={topic.videoType}
                                    poster={
                                        topic.thumbnailUrl
                                            ? `${process.env.PUBLIC_URL}/help/${topic.thumbnailUrl}`
                                            : undefined
                                    }
                                />
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        background: '#000',
                                        // mixBlendMode: 'multiply',
                                        opacity: 0.2,
                                        pointerEvents: 'none',
                                        borderRadius: '4px',
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: 'transparent',
                                    zIndex: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                }}
                            >
                                <div
                                    style={{
                                        transition: 'all 0.2s ease',
                                        pointerEvents: 'auto',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform =
                                            'scale(1.1)'
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform =
                                            'scale(1)'
                                    }}
                                >
                                    <svg
                                        width="100"
                                        height="100"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <ellipse
                                            cx="12.5"
                                            cy="12"
                                            rx="5.5"
                                            ry="5"
                                            fill="#fff"
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M2.25 12C2.25 6.615 6.615 2.25 12 2.25C17.385 2.25 21.75 6.615 21.75 12C21.75 17.385 17.385 21.75 12 21.75C6.615 21.75 2.25 17.385 2.25 12ZM16.274 11.017C16.4492 11.1145 16.5951 11.257 16.6967 11.4299C16.7983 11.6027 16.8519 11.7995 16.8519 12C16.8519 12.2005 16.7983 12.3973 16.6967 12.5701C16.5951 12.743 16.4492 12.8855 16.274 12.983L10.671 16.096C10.4998 16.191 10.3067 16.2397 10.1109 16.2373C9.91513 16.2348 9.72336 16.1813 9.55458 16.082C9.3858 15.9828 9.24586 15.8411 9.14859 15.6712C9.05133 15.5012 9.00011 15.3088 9 15.113V8.887C9 8.03 9.921 7.487 10.671 7.904L16.274 11.017Z"
                                            fill="var(--theme-primary)"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Skeleton variant="rectangular" width="100%" />
                    )}

                    {disabledMessage && (
                        <div className="emp-video-disabled">
                            <Typography variant="h3">
                                {disabledMessage}
                            </Typography>
                        </div>
                    )}
                </CardMedia>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    component="div"
                    gutterBottom
                    className={
                        'emp-typography c-help-center-topic__description'
                    }
                >
                    {loading
                        ? [...Array(5)].map((_, index) => (
                              <div
                                  key={`skeleton-${
                                      topic.id
                                  }-${Math.random()}-${index}`}
                              >
                                  <Skeleton variant="text" />
                              </div>
                          ))
                        : topic?.description}
                </Typography>

                {topic?.outro && <div>{topic?.outro}</div>}
            </AccentArea>

            <VideoModal
                open={isModalOpen}
                onClose={handleCloseModal}
                topic={topic}
            />
        </div>
    )
}

export default TopicArea
